/**
 * The workspace route: the ONLY place modules meet (law 3). The shell
 * owns the frame and the projectId; ingest and viewer receive it as a
 * prop and never learn about each other.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ProjectTabs } from './ProjectTabs';
import { subscribeStreamConnected } from '../api';
import { signOutUser, type User } from '../modules/auth';
import { IfcPreview, UploadPanel } from '../modules/ingest';
import { ViewerPanel, useElementsStatus } from '../modules/viewer';
import { ActionButton, Field, RouteLink, Stack, StatusDot, Text, Title, Workbench } from '../strata';

function StreamStatus() {
  const [connected, setConnected] = useState(false);
  useEffect(() => subscribeStreamConnected(setConnected), []);
  // "Reconnecting" is the literal truth: the stream retries with
  // backoff forever, and a successful reconnect refetches everything.
  return <StatusDot live={connected} label={connected ? 'Live updates connected' : 'Reconnecting to live updates…'} />;
}

function ElementsStatus({ projectId }: { projectId: string }) {
  const { count, isError, isSettled, refusedIdentity } = useElementsStatus(projectId);
  // The stack STAYS UP (ruled 2026-08-25), so an unreachable server is
  // a fault rather than a sleeping VM. Wake-on-signin is retired: this
  // used to POST a wake endpoint on error and tell the reader to wait
  // two minutes, which is the wrong thing to say about a server that
  // should be answering.
  const label = isSettled
    ? `${count} elements loaded`
    : isError
      ? refusedIdentity
        ? 'Your sign-in was refused. Sign in again, or ask for access to this record.'
        : 'Cannot reach the server.'
      : 'Connecting to the server…';
  return <StatusDot live={isSettled} label={label} />;
}

export function Workspace({ user, projectId }: { user: User; projectId: string }) {
  // The project id is the ROUTE's (APP_SPEC box 1): the input edits a
  // local buffer and navigates on every non-empty value (replace, so
  // keystrokes never stack in history). An emptied input stays local
  // until something is typed -- the URL always names a real project.
  const navigate = useNavigate();
  const [projectInput, setProjectInput] = useState(projectId);
  useEffect(() => setProjectInput(projectId), [projectId]);
  const onProjectInput = (value: string) => {
    setProjectInput(value);
    if (value)
      void navigate({
        to: '/projects/$projectId',
        params: { projectId: value },
        replace: true,
      });
  };
  // The instant preview owns the MAIN canvas while a picked file is
  // provisional: ingest elects the file (onPreview), the shell places
  // it over the viewer, and 'parsed' hands up null -- colored server
  // truth replaces the local gray without a click.
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  const rail = (
    <>
      <RouteLink to="/projects">← All projects</RouteLink>
      <Title level={3}>Polywog</Title>
      <ProjectTabs projectId={projectId} active="viewer" />
      <Stack direction="row" gap="sm" align="center">
        <Text size="caption" tone="secondary" ellipsis>
          {user.email ?? user.uid}
        </Text>
        <ActionButton size="sm" onPress={() => void signOutUser()}>
          Sign out
        </ActionButton>
      </Stack>
      <ElementsStatus projectId={projectId} />
      <StreamStatus />
      <Stack direction="column" gap="sm">
        <Field label="Project id" labelHidden value={projectInput} onChange={onProjectInput} placeholder="project_id" mono />
        <UploadPanel projectId={projectId} onPreview={setPreviewFile} />
      </Stack>
    </>
  );

  return (
    <Workbench rail={rail}>
      <ViewerPanel projectId={projectId} />
      {previewFile && (
        <IfcPreview
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          onFailed={(reason) => {
            console.warn(`web-ifc preview declined (${reason}); websocket flow is the UX`);
            setPreviewFile(null);
          }}
        />
      )}
    </Workbench>
  );
}
